import { useEffect, useState, useCallback } from 'react';

/**
 * FacilityHoursAdmin
 * Scheduler admin panel: gear → ADMIN → Facility Hours
 * Reads from dbo.Facilities (via mgmt/facilities) — no dock.Facility.
 */

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_ABBR  = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const SQL_DAYS  = [1,2,3,4,5,6,7];

interface HoursRow {
    operatingHoursId?: number;
    dayOfWeek:          number;
    direction:          'INBOUND' | 'OUTBOUND' | 'BOTH';
    isOpen:             boolean;
    openTimeMinutes:    number | null;
    closeTimeMinutes:   number | null;
    defaultSlotMinutes: number | null;
    notes:              string;
    _key:               string;
}

interface Facility {
    FacilityCode: string;
    DisplayName:  string;
    Timezone:     string;
    WmsType:      string;
}

interface Holiday {
    HolidayDate: string;
    HolidayName: string;
    IsGlobal:    boolean;
}

function minutesToTime(m: number | null): string {
    if (m === null) return '';
    return `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;
}
function timeToMinutes(t: string): number | null {
    if (!t) return null;
    const [h,m] = t.split(':').map(Number);
    return isNaN(h)||isNaN(m) ? null : h*60+m;
}
function defaultRow(dow: number, direction: 'BOTH'|'INBOUND'|'OUTBOUND'='BOTH'): HoursRow {
    const weekend = dow===1||dow===7;
    return { dayOfWeek: dow, direction, isOpen: !weekend,
             openTimeMinutes: weekend?null:360, closeTimeMinutes: weekend?null:1080,
             defaultSlotMinutes: 60, notes: '', _key: `${dow}:${direction}` };
}
function buildDefaultSchedule(): HoursRow[] { return SQL_DAYS.map(d=>defaultRow(d,'BOTH')); }

async function apiFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`/api/${path}`, {
        ...options, credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token, ...(options?.headers??{}) },
    });
    if (!res.ok) { const e = await res.json().catch(()=>({error:res.statusText})); throw new Error(e.error??`HTTP ${res.status}`); }
    if (res.status===204) return undefined as unknown as T;
    return res.json();
}

// ── Concurrency limits (Feature C) ───────────────────────────────────────────
interface CapBand {
    daysOfWeek: number[];
    direction: 'INBOUND' | 'OUTBOUND' | 'BOTH';
    startMinutes: number;
    endMinutes: number;
    maxConcurrent: number | null;
    /**
     * ⭐⭐ migration 188. false = maxConcurrent is ONE cap across the whole band
     * (what every band meant before 188). true = it is a cap on EACH clock hour
     * inside the band, and a load consumes every hour it touches.
     *
     * ⛔ Pete, 2026-09-22: "I say 3 and then say 8am to 5pm. That tells you that
     * everyhour starting at 8, you can only have 3 trucks."
     */
    perHour: boolean;
    isActive: boolean;
    effectiveStartDate: string | null;   // YYYY-MM-DD, null = recurring
    effectiveEndDate: string | null;
    _key: string;
}
// App weekday convention: 1=Sun .. 7=Sat
const weekdayOf = (dateStr: string): number => new Date(`${dateStr}T12:00:00Z`).getUTCDay() + 1;
/**
 * ---- ⛔⛔ THE SAME WHOLE-HOUR RULE THE VALIDATOR USES ---------------------
 *
 * `facilitySchedule.ts` counts hours as `ceil(start/60) .. floor(end/60)-1`:
 * a band of 08:00-17:00 caps the nine hours 8..16, and a band ending 16:30 does
 * NOT cap a half-hour as if it were a whole one.
 *
 * ⚠️ THIS IS A SECOND COPY OF THAT ARITHMETIC AND THAT IS A KNOWN COST. The
 * alternative was shipping a number from the server for a value the user is
 * still typing, which cannot be done without a round trip per keystroke.
 *   ** WHEN A RULE MUST EXIST IN TWO PLACES, WRITE DOWN WHERE THE OTHER ONE IS.
 *      A DUPLICATE NOBODY CAN FIND IS A DUPLICATE NOBODY UPDATES. **
 * ⛔ If this changes, change `evaluateFacilityCap`'s per-hour branch with it.
 */
function cappedHourCount(startMinutes: number, endMinutes: number): number {
    const first = Math.ceil(startMinutes / 60);
    const last  = Math.floor(endMinutes / 60) - 1;
    return Math.max(0, last - first + 1);
}

let capKeySeq = 0;
const newCapKey = () => `cap-${Date.now()}-${capKeySeq++}`;

// ── Door dedications (Feature B) ─────────────────────────────────────────────
interface DedRow {
    dockDoorId: number | null;
    targetType: 'CARRIER' | 'CLIENT';
    targetCode: string;
    targetLabel: string | null;
    daysOfWeek: number[];         // full week [1..7] = every day
    startMinutes: number | null;  // null = all day
    endMinutes: number | null;
    isActive: boolean;
    effectiveStartDate: string | null;   // null = recurring
    effectiveEndDate: string | null;
    _key: string;
}
let dedKeySeq = 0;
const newDedKey = () => `ded-${Date.now()}-${dedKeySeq++}`;

/** A compact Su–Sa multi-select. `days` is a sorted list of 1..7 (1=Sun). */
function DayToggles({ days, onChange }: { days: number[]; onChange: (days: number[]) => void }) {
    const toggle = (d: number) =>
        onChange(days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort((a, b) => a - b));
    return (
        <div className="flex gap-0.5">
            {SQL_DAYS.map(d => {
                const on = days.includes(d);
                return (
                    <button
                        key={d}
                        type="button"
                        onClick={() => toggle(d)}
                        title={DAY_NAMES[d - 1]}
                        className={`w-6 h-7 rounded text-[11px] font-medium border transition-colors ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}
                    >
                        {DAY_ABBR[d - 1]}
                    </button>
                );
            })}
        </div>
    );
}

function FacilityCapacityEditor({ facilityCode, getToken }: { facilityCode: string; getToken: () => Promise<string> }) {
    const [bands, setBands]     = useState<CapBand[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState<string | null>(null);
    const [saved, setSaved]     = useState(false);

    useEffect(() => {
        if (!facilityCode) { setBands([]); return; }
        setLoading(true); setError(null); setSaved(false);
        getToken()
            .then(t => apiFetch<{ rows: { dayOfWeek: number; direction: 'INBOUND' | 'OUTBOUND' | 'BOTH'; startMinutes: number; endMinutes: number; maxConcurrent: number; perHour: boolean; isActive: boolean; effectiveStartDate: string | null; effectiveEndDate: string | null }[] }>(`mgmt/facilities/${facilityCode}/capacity`, t))
            .then(data => {
                // Collapse identical per-day rows into one multi-day band.
                const groups = new Map<string, CapBand>();
                for (const r of data.rows) {
                    const active = r.isActive !== false;
                    const es = r.effectiveStartDate ?? null, ee = r.effectiveEndDate ?? null;
                    /* ⛔⛔ perHour IS PART OF THE GROUPING KEY. This map collapses
                       identical per-day rows into one multi-day band; two rows
                       differing ONLY in perHour are NOT the same band, and merging
                       them would silently adopt whichever the loop saw first.
                         ** A GROUPING KEY THAT OMITS A FIELD DECLARES THAT FIELD
                            IRRELEVANT TO IDENTITY. ** */
                    const ph = r.perHour === true;
                    const k = `${r.direction}|${r.startMinutes}|${r.endMinutes}|${r.maxConcurrent}|${ph}|${active}|${es}|${ee}`;
                    const ex = groups.get(k);
                    if (ex) ex.daysOfWeek.push(r.dayOfWeek);
                    else groups.set(k, { daysOfWeek: [r.dayOfWeek], direction: r.direction, startMinutes: r.startMinutes, endMinutes: r.endMinutes, maxConcurrent: r.maxConcurrent, perHour: ph, isActive: active, effectiveStartDate: es, effectiveEndDate: ee, _key: newCapKey() });
                }
                for (const b of groups.values()) b.daysOfWeek.sort((a, b) => a - b);
                setBands(Array.from(groups.values()));
            })
            .catch(e => setError(e instanceof Error ? e.message : 'Could not load limits.'))
            .finally(() => setLoading(false));
    // Reload only when the facility changes — NOT on every parent re-render
    // (e.g. saving hours), which would wipe unsaved band edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityCode]);

    const addBand = () => {
        setSaved(false);
        setBands(bs => [...bs, { daysOfWeek: [2], direction: 'INBOUND', startMinutes: 8 * 60, endMinutes: 17 * 60, maxConcurrent: null, perHour: false, isActive: true, effectiveStartDate: null, effectiveEndDate: null, _key: newCapKey() }]);
    };
    const removeBand = (key: string) => { setSaved(false); setBands(bs => bs.filter(b => b._key !== key)); };
    const updateBand = (key: string, patch: Partial<CapBand>) => {
        setSaved(false);
        setBands(bs => bs.map(b => (b._key === key ? { ...b, ...patch } : b)));
    };

    const handleSave = async () => {
        for (const b of bands) {
            if (b.daysOfWeek.length === 0) { setError('Pick at least one day for every band.'); return; }
            if (b.endMinutes <= b.startMinutes) { setError('Each band must end after it starts.'); return; }
            if (b.maxConcurrent === null || b.maxConcurrent < 0) { setError('Set a "Max at once" (0 or more) for every band. Use 0 to block that window to everyone.'); return; }
        }
        setSaving(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${facilityCode}/capacity`, token, {
                method: 'PUT',
                body: JSON.stringify({
                    // Expand each multi-day band into one row per day.
                    rows: bands.flatMap(b => b.daysOfWeek.map(d => ({
                        dayOfWeek: d, direction: b.direction, startMinutes: b.startMinutes,
                        endMinutes: b.endMinutes, maxConcurrent: b.maxConcurrent as number,
                        perHour: b.perHour,
                        isActive: b.isActive,
                        effectiveStartDate: b.effectiveStartDate, effectiveEndDate: b.effectiveEndDate,
                    }))),
                }),
            });
            setSaved(true);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Save failed.');
        } finally { setSaving(false); }
    };

    if (!facilityCode) return null;

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
            <div>
                <h3 className="text-sm font-semibold text-gray-800">Appointment limits</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                    Two kinds of cap. <strong>At once</strong> limits how many appointments may overlap facility-wide inside the band. <strong>Per hour</strong> makes the number a cap on each clock hour in the band, and an appointment counts against every hour it touches. Carriers can't book past either; schedulers get an overridable warning. No bands = unlimited. Where bands overlap, the lowest limit wins.
                </p>
            </div>

            {loading ? (
                <p className="text-xs text-gray-400">Loading…</p>
            ) : (
                <>
                    {bands.length === 0 ? (
                        <p className="text-xs text-gray-400">No limits set — this facility is unlimited.</p>
                    ) : (
                        <div className="space-y-2">
                            <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto_auto] gap-2 items-center text-[10px] uppercase tracking-wide text-gray-400 px-1">
                                <span>Day</span><span>Direction</span><span>From</span><span>To</span><span>Max</span><span>Per hour</span><span>On</span><span></span>
                            </div>
                            {bands.map(b => (
                                <div key={b._key} className={`rounded border border-gray-200 p-2 space-y-1.5 ${b.isActive ? '' : 'opacity-60'}`}>
                                  <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto_auto] gap-2 items-center">
                                    <DayToggles days={b.daysOfWeek} onChange={days => updateBand(b._key, { daysOfWeek: days })} />
                                    <select value={b.direction} onChange={e => updateBand(b._key, { direction: e.target.value as CapBand['direction'] })}
                                        className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                        <option value="INBOUND">Inbound</option>
                                        <option value="OUTBOUND">Outbound</option>
                                        <option value="BOTH">Both</option>
                                    </select>
                                    <input type="time" value={minutesToTime(b.startMinutes)} onChange={e => updateBand(b._key, { startMinutes: timeToMinutes(e.target.value) ?? 0 })}
                                        className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                    <input type="time" value={minutesToTime(b.endMinutes)} onChange={e => updateBand(b._key, { endMinutes: timeToMinutes(e.target.value) ?? 0 })}
                                        className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                    <input type="number" min={0} max={999} value={b.maxConcurrent ?? ''} placeholder="—"
                                        title="Appointments allowed at once. Set 0 to block this window to everyone (carriers and schedulers)."
                                        onChange={e => { const v = parseInt(e.target.value, 10); updateBand(b._key, { maxConcurrent: Number.isFinite(v) ? v : null }); }}
                                        className={`w-20 rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${b.maxConcurrent === 0 ? 'border-amber-400 bg-amber-50 text-amber-800 font-medium' : 'border-gray-300'}`} />
                                    {/* ⭐ migration 188. Violet deliberately: the board draws
                                        per-hour bands in violet too, so the two screens agree
                                        about which rule a band carries without reading a word. */}
                                    <button
                                        onClick={() => updateBand(b._key, { perHour: !b.perHour })}
                                        title={b.perHour
                                            ? 'Per hour: the number is a cap on EACH clock hour in the band. An appointment counts against every hour it touches. Click to make it one cap across the whole band.'
                                            : 'One cap across the whole band (how many may overlap at once). Click to make it a cap per clock hour.'}
                                        className={`px-2 py-1 rounded text-[10px] font-semibold border ${b.perHour ? 'bg-violet-100 text-violet-800 border-violet-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}
                                    >
                                        {b.perHour ? 'Per hour' : 'At once'}
                                    </button>
                                    <button
                                        onClick={() => updateBand(b._key, { isActive: !b.isActive })}
                                        title={b.isActive ? 'Active — click to turn off (keeps the rule for later)' : 'Inactive — click to turn on'}
                                        className={`px-2 py-1 rounded text-[10px] font-semibold border ${b.isActive ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}
                                    >
                                        {b.isActive ? 'On' : 'Off'}
                                    </button>
                                    <button onClick={() => removeBand(b._key)} className="text-gray-400 hover:text-red-600 text-base px-1 leading-none" title="Remove">×</button>
                                  </div>
                                  {/* ⛔⛔ THE BAND SAYS WHAT IT PERMITS, IN WORDS, WHERE IT IS
                                      CONFIGURED. Pete asked for a day total; the honest place
                                      for it is next to the numbers that produce it, not in a
                                      header that has to guess which bands apply today.
                                        ** A TOTAL SHOWN AWAY FROM ITS INPUTS IS A NUMBER
                                           NOBODY CAN CHECK. ** */}
                                  {b.perHour && b.maxConcurrent !== null && (
                                    <div className="text-[11px] text-violet-700 pl-1">
                                        {cappedHourCount(b.startMinutes, b.endMinutes) === 0
                                            ? 'This band covers no whole clock hour, so it caps nothing. Widen it to a full hour.'
                                            : `${b.maxConcurrent} per hour over ${cappedHourCount(b.startMinutes, b.endMinutes)} hour${cappedHourCount(b.startMinutes, b.endMinutes) === 1 ? '' : 's'} = ${b.maxConcurrent * cappedHourCount(b.startMinutes, b.endMinutes)} for the day. An appointment counts against every hour it touches.`}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-[11px] text-gray-500 pl-1">
                                    <span className="uppercase tracking-wide text-gray-400">Dates</span>
                                    <input type="date" value={b.effectiveStartDate ?? ''}
                                        title="Effective from (blank = recurring / always)"
                                        onChange={e => {
                                            const v = e.target.value || null;
                                            const patch: Partial<CapBand> = { effectiveStartDate: v };
                                            // Single-date convenience: if the end is empty, mirror it and
                                            // pin the weekday to that date so a one-day block always applies.
                                            if (v && !b.effectiveEndDate) { patch.effectiveEndDate = v; patch.daysOfWeek = [weekdayOf(v)]; }
                                            updateBand(b._key, patch);
                                        }}
                                        className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                    <span>to</span>
                                    <input type="date" value={b.effectiveEndDate ?? ''}
                                        title="Effective to (blank = no end)"
                                        onChange={e => updateBand(b._key, { effectiveEndDate: e.target.value || null })}
                                        className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                    {!b.effectiveStartDate && !b.effectiveEndDate
                                        ? <span className="text-gray-400">recurring (every selected weekday)</span>
                                        : b.effectiveStartDate && b.effectiveStartDate === b.effectiveEndDate
                                            ? <span className="text-amber-700 font-medium">single date</span>
                                            : <span className="text-gray-400">date range</span>}
                                    {(b.effectiveStartDate || b.effectiveEndDate) && (
                                        <button onClick={() => updateBand(b._key, { effectiveStartDate: null, effectiveEndDate: null })}
                                            className="text-gray-400 hover:text-gray-700 underline">clear</button>
                                    )}
                                  </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-1">
                        <button onClick={addBand} className="text-xs text-blue-600 hover:text-blue-800 underline">+ Add band</button>
                        <div className="flex items-center gap-3">
                            {saved && <span className="text-xs text-green-600">Saved</span>}
                            {error && <span className="text-xs text-red-600">{error}</span>}
                            <button onClick={handleSave} disabled={saving}
                                className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors">
                                {saving ? 'Saving…' : 'Save limits'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function FacilityDedicationsEditor({ facilityCode, getToken }: { facilityCode: string; getToken: () => Promise<string> }) {
    const [rows, setRows]         = useState<DedRow[]>([]);
    const [doors, setDoors]       = useState<{ DockDoorId: number; Code: string; Name: string }[]>([]);
    const [carriers, setCarriers] = useState<{ CarrierCode: string; CarrierName: string }[]>([]);
    const [clients, setClients]   = useState<{ ClientCode: string; ClientName: string }[]>([]);
    const [loading, setLoading]   = useState(false);
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState<string | null>(null);
    const [saved, setSaved]       = useState(false);

    useEffect(() => {
        if (!facilityCode) { setRows([]); setDoors([]); return; }
        let cancelled = false;
        setLoading(true); setError(null); setSaved(false);

        getToken().then(t => {
            // Load each independently — a missing endpoint (e.g. dedications not
            // deployed yet) must not blank the door list and grey out the button.
            const doorsP = apiFetch<{ doors: { DockDoorId: number; Code: string; Name: string }[] }>(`mgmt/doors?facilityCode=${facilityCode}`, t)
                .then(d => { if (!cancelled) setDoors(d.doors); })
                .catch(() => { throw new Error('Could not load this facility\u2019s doors.'); });

            const lookupsP = apiFetch<{ carriers: { CarrierCode: string; CarrierName: string }[]; clients: { ClientCode: string; ClientName: string }[] }>(`lookups`, t)
                .then(l => { if (!cancelled) { setCarriers(l.carriers); setClients(l.clients); } })
                .catch(() => { throw new Error('Could not load carriers/clients.'); });

            const dedP = apiFetch<{ rows: { dockDoorId: number; targetType: 'CARRIER' | 'CLIENT'; targetCode: string; targetLabel: string | null; dayOfWeek: number | null; startMinutes: number | null; endMinutes: number | null; isActive: boolean; effectiveStartDate: string | null; effectiveEndDate: string | null }[] }>(`mgmt/facilities/${facilityCode}/dedications`, t)
                .then(d => {
                    if (cancelled) return;
                    // Collapse identical rows across days into one multi-day row.
                    // A saved null day = every day -> full week.
                    const groups = new Map<string, DedRow & { _everyday?: boolean }>();
                    for (const r of d.rows) {
                        const active = r.isActive !== false;
                        const es = r.effectiveStartDate ?? null, ee = r.effectiveEndDate ?? null;
                        const k = `${r.dockDoorId}|${r.targetType}|${r.targetCode}|${r.startMinutes}|${r.endMinutes}|${active}|${es}|${ee}`;
                        let g = groups.get(k);
                        if (!g) {
                            g = { dockDoorId: r.dockDoorId, targetType: r.targetType, targetCode: r.targetCode, targetLabel: r.targetLabel, daysOfWeek: [], startMinutes: r.startMinutes, endMinutes: r.endMinutes, isActive: active, effectiveStartDate: es, effectiveEndDate: ee, _key: newDedKey() };
                            groups.set(k, g);
                        }
                        if (r.dayOfWeek == null) g._everyday = true;
                        else g.daysOfWeek.push(r.dayOfWeek);
                    }
                    const out = Array.from(groups.values()).map(g => {
                        const days = g._everyday ? [...SQL_DAYS] : g.daysOfWeek.sort((a, b) => a - b);
                        return { dockDoorId: g.dockDoorId, targetType: g.targetType, targetCode: g.targetCode, targetLabel: g.targetLabel, daysOfWeek: days, startMinutes: g.startMinutes, endMinutes: g.endMinutes, isActive: g.isActive, effectiveStartDate: g.effectiveStartDate, effectiveEndDate: g.effectiveEndDate, _key: g._key };
                    });
                    setRows(out);
                })
                .catch(() => { throw new Error('Could not load saved dedications \u2014 are the dedication endpoints deployed?'); });

            // Settle all; report the first failure but keep whatever succeeded.
            return Promise.allSettled([doorsP, lookupsP, dedP]).then(results => {
                if (cancelled) return;
                const firstFail = results.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
                if (firstFail) setError(firstFail.reason instanceof Error ? firstFail.reason.message : 'Some data could not be loaded.');
            });
        })
        .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load dedications.'); })
        .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    // Reload only when the facility changes (see capacity editor note).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityCode]);

    const addRow = () => {
        setSaved(false);
        setRows(rs => [...rs, { dockDoorId: doors[0]?.DockDoorId ?? null, targetType: 'CARRIER', targetCode: '', targetLabel: null, daysOfWeek: [...SQL_DAYS], startMinutes: null, endMinutes: null, isActive: true, effectiveStartDate: null, effectiveEndDate: null, _key: newDedKey() }]);
    };
    const removeRow = (key: string) => { setSaved(false); setRows(rs => rs.filter(r => r._key !== key)); };
    const updateRow = (key: string, patch: Partial<DedRow>) => { setSaved(false); setRows(rs => rs.map(r => (r._key === key ? { ...r, ...patch } : r))); };

    const pickTarget = (key: string, type: 'CARRIER' | 'CLIENT', code: string) => {
        const label = type === 'CARRIER'
            ? (carriers.find(c => c.CarrierCode === code)?.CarrierName ?? null)
            : (clients.find(c => c.ClientCode === code)?.ClientName ?? null);
        updateRow(key, { targetCode: code, targetLabel: label });
    };
    const setType   = (key: string, type: 'CARRIER' | 'CLIENT') => updateRow(key, { targetType: type, targetCode: '', targetLabel: null });
    const setAllDay = (key: string, allDay: boolean) => updateRow(key, allDay ? { startMinutes: null, endMinutes: null } : { startMinutes: 8 * 60, endMinutes: 17 * 60 });

    const handleSave = async () => {
        for (const r of rows) {
            if (r.dockDoorId == null) { setError('Pick a door for every dedication.'); return; }
            if (!r.targetCode)        { setError('Pick a carrier or client for every dedication.'); return; }
            if (r.daysOfWeek.length === 0) { setError('Pick at least one day for every dedication.'); return; }
            if (r.startMinutes != null && r.endMinutes != null && r.endMinutes <= r.startMinutes) { setError('Each time window must end after it starts.'); return; }
        }
        setSaving(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${facilityCode}/dedications`, token, {
                method: 'PUT',
                body: JSON.stringify({
                    // Full week -> one "every day" row (null); otherwise one row per day.
                    rows: rows.flatMap(r => {
                        const daysToSave: (number | null)[] = r.daysOfWeek.length === 7 ? [null] : r.daysOfWeek;
                        return daysToSave.map(d => ({
                            dockDoorId: r.dockDoorId, targetType: r.targetType, targetCode: r.targetCode,
                            targetLabel: r.targetLabel, dayOfWeek: d,
                            startMinutes: r.startMinutes, endMinutes: r.endMinutes,
                            isActive: r.isActive,
                            effectiveStartDate: r.effectiveStartDate, effectiveEndDate: r.effectiveEndDate,
                        }));
                    }),
                }),
            });
            setSaved(true);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Save failed.');
        } finally { setSaving(false); }
    };

    if (!facilityCode) return null;

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
            <div>
                <h3 className="text-sm font-semibold text-gray-800">Door dedications</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                    Reserve a door for a carrier or client. Schedulers assigning someone else to that door get an overridable warning. Leave the day as &ldquo;Every day&rdquo; and the window as &ldquo;All day&rdquo; for a standing reservation. Carriers never pick doors, so this only affects schedulers.
                </p>
            </div>

            {loading ? (
                <p className="text-xs text-gray-400">Loading…</p>
            ) : (
                <>
                    {rows.length === 0 ? (
                        <p className="text-xs text-gray-400">No dedications — any carrier/client can use any door.</p>
                    ) : (
                        <div className="space-y-2">
                            {rows.map(r => {
                                const allDay = r.startMinutes == null;
                                const targets = r.targetType === 'CARRIER'
                                    ? carriers.map(c => ({ code: c.CarrierCode, name: c.CarrierName }))
                                    : clients.map(c => ({ code: c.ClientCode, name: c.ClientName }));
                                return (
                                <div key={r._key} className={`rounded-lg border border-gray-100 bg-gray-50/50 p-2 space-y-2 ${r.isActive ? '' : 'opacity-60'}`}>
                                    <div className="flex items-center gap-2">
                                        <select value={r.dockDoorId ?? ''} onChange={e => updateRow(r._key, { dockDoorId: e.target.value ? parseInt(e.target.value, 10) : null })}
                                            className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                            <option value="" disabled>—</option>
                                            {doors.map(d => <option key={d.DockDoorId} value={d.DockDoorId}>{d.Code}{d.Name ? ` · ${d.Name}` : ''}</option>)}
                                        </select>
                                        <select value={r.targetType} onChange={e => setType(r._key, e.target.value as 'CARRIER' | 'CLIENT')}
                                            className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                            <option value="CARRIER">Carrier</option>
                                            <option value="CLIENT">Client</option>
                                        </select>
                                        <select value={r.targetCode} onChange={e => pickTarget(r._key, r.targetType, e.target.value)}
                                            className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                            <option value="" disabled>Select…</option>
                                            {targets.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
                                        </select>
                                        <label className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                                            <input type="checkbox" checked={allDay} onChange={e => setAllDay(r._key, e.target.checked)} className="h-4 w-4" />
                                            All day
                                        </label>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <input type="time" disabled={allDay} value={minutesToTime(r.startMinutes ?? 8 * 60)} onChange={e => updateRow(r._key, { startMinutes: timeToMinutes(e.target.value) ?? 0 })}
                                                className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                            <span className="text-gray-400 text-xs">–</span>
                                            <input type="time" disabled={allDay} value={minutesToTime(r.endMinutes ?? 17 * 60)} onChange={e => updateRow(r._key, { endMinutes: timeToMinutes(e.target.value) ?? 0 })}
                                                className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                        </div>
                                        <button
                                            onClick={() => updateRow(r._key, { isActive: !r.isActive })}
                                            title={r.isActive ? 'Active — click to turn off (keeps the dedication for later)' : 'Inactive — click to turn on'}
                                            className={`px-2 py-1 rounded text-[10px] font-semibold border shrink-0 ${r.isActive ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}
                                        >
                                            {r.isActive ? 'On' : 'Off'}
                                        </button>
                                        <button onClick={() => removeRow(r._key)} className="text-gray-400 hover:text-red-600 text-base px-1 leading-none shrink-0" title="Remove">×</button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-wide text-gray-400 w-14 shrink-0">Days</span>
                                        <DayToggles days={r.daysOfWeek} onChange={days => updateRow(r._key, { daysOfWeek: days })} />
                                    </div>
                                    <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                        <span className="text-[10px] uppercase tracking-wide text-gray-400 w-14 shrink-0">Dates</span>
                                        <input type="date" value={r.effectiveStartDate ?? ''}
                                            title="Effective from (blank = recurring / always)"
                                            onChange={e => {
                                                const v = e.target.value || null;
                                                const patch: Partial<DedRow> = { effectiveStartDate: v };
                                                if (v && !r.effectiveEndDate) { patch.effectiveEndDate = v; patch.daysOfWeek = [weekdayOf(v)]; }
                                                updateRow(r._key, patch);
                                            }}
                                            className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                        <span>to</span>
                                        <input type="date" value={r.effectiveEndDate ?? ''}
                                            title="Effective to (blank = no end)"
                                            onChange={e => updateRow(r._key, { effectiveEndDate: e.target.value || null })}
                                            className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                        {!r.effectiveStartDate && !r.effectiveEndDate
                                            ? <span className="text-gray-400">recurring</span>
                                            : r.effectiveStartDate && r.effectiveStartDate === r.effectiveEndDate
                                                ? <span className="text-amber-700 font-medium">single date</span>
                                                : <span className="text-gray-400">date range</span>}
                                        {(r.effectiveStartDate || r.effectiveEndDate) && (
                                            <button onClick={() => updateRow(r._key, { effectiveStartDate: null, effectiveEndDate: null })}
                                                className="text-gray-400 hover:text-gray-700 underline">clear</button>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-1">
                        <button onClick={addRow} disabled={doors.length === 0}
                            className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-40 disabled:no-underline">+ Add dedication</button>
                        <div className="flex items-center gap-3">
                            {saved && <span className="text-xs text-green-600">Saved</span>}
                            {error && <span className="text-xs text-red-600">{error}</span>}
                            <button onClick={handleSave} disabled={saving}
                                className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors">
                                {saving ? 'Saving…' : 'Save dedications'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}


/**
 * ⭐ #145 — HOW FAR BACK AND FORWARD AN APPOINTMENT SEARCH LOOKS.
 *
 * Pete, 2026-08-24: *"Lets do 90/90 days default but have this in a config screen"*.
 *
 * ⛔ EMPTY IS A REAL, SAVEABLE VALUE and it means "use the shipped default". Clearing
 * the box sends `null`, not 0 and not 90. ⚠️ A typed 90 and an unset 90 look identical
 * on screen today and mean different things the day the default changes — which is why
 * the default appears as a PLACEHOLDER and never as a pre-filled value.
 *
 * ⚠️ USES THIS FILE'S OWN apiFetch AND PLAIN ELEMENTS, not the shadcn Button/Input and
 * not @/api/client. Both work; matching the file it lives in is what keeps a panel from
 * looking like it was bolted on.
 */
function SearchWindowEditor(
    { facilityCode, getToken }: { facilityCode: string; getToken: () => Promise<string> },
) {
    const [text, setText] = useState('');
    const [defaultDays, setDefaultDays] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!facilityCode) return;
        setLoading(true);
        setError(null);
        getToken()
            .then(t => apiFetch<{ searchWindowDays: number | null; defaultWindowDays: number }>(
                `mgmt/facilities/${facilityCode}/search-window`, t))
            .then(r => {
                setText(r.searchWindowDays === null ? '' : String(r.searchWindowDays));
                setDefaultDays(r.defaultWindowDays);
            })
            .catch(e => setError(e instanceof Error ? e.message : 'Failed to load the search window.'))
            .finally(() => setLoading(false));
    }, [facilityCode, getToken]);

    /**
     * ⚠️ THE RANGE MATCHES migration 160's CHECK CONSTRAINT AND THE ROUTE'S ZOD SCHEMA
     * (1..3650). Three copies of one rule is two too many, but the alternative — a
     * screen that accepts what the database rejects — reaches the scheduler as a raw
     * driver error on save. ⛔ If one of the three changes, all three change.
     */
    const trimmed = text.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    const invalid = parsed !== null && (!Number.isInteger(parsed) || parsed < 1 || parsed > 3650);

    async function save() {
        if (invalid) return;
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${facilityCode}/search-window`, token, {
                method: 'PUT',
                body: JSON.stringify({ searchWindowDays: parsed }),
            });
            setSaved(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="rounded-md border border-gray-200 p-3 space-y-2">
            <div className="text-sm font-medium">Appointment search window</div>
            <p className="text-[11px] text-gray-500">
                How many days either side of the day being viewed the search box looks.
                A scheduler can always pick “Search all dates” from the results panel.
            </p>
            <div className="flex items-center gap-2">
                <input
                    type="number" min={1} max={3650} value={text}
                    placeholder={defaultDays === null ? '—' : `${defaultDays} (default)`}
                    title="Leave empty to use the shipped default."
                    disabled={loading || saving}
                    onChange={e => { setText(e.target.value); setSaved(false); }}
                    className={`w-40 rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${invalid ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                />
                <span className="text-[11px] text-gray-500">days either side</span>
                <button
                    onClick={() => void save()}
                    disabled={saving || loading || invalid}
                    className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {saved && <span className="text-[11px] text-green-700">Saved</span>}
            </div>
            {invalid && (
                <div className="text-[11px] text-red-600">
                    Enter a whole number between 1 and 3650, or leave it empty to use the default.
                </div>
            )}
            {error && <div className="text-[11px] text-red-600">{error}</div>}
        </div>
    );
}

/**
 * ⭐ F103 — DETENTION (Decision 280, migration 224).
 *
 * > Pete: *"2 hour turn is called detention I think. We definitely need to track that
 * > on trucks"* → *"Live on the truck + stored"*.
 *
 * ⭐ F155 (Decision 329, migration 229) — THREE numbers, and the clock COUNTS DOWN:
 * > *"the detention clock should start at his appointment time and count down from
 * > there. Should only be 1-hour to unload/load the truck. Turn orange at 30 and red
 * > at 15"*.
 *   · Time to load or unload — minutes before a truck is in detention (default 120 since
 *     F173; ⛔⤴ ~~60~~). A client's own time (DetentionRulesEditor) replaces it for that client.
 *     0 turns the clock off here.
 *   · Orange at — minutes LEFT when the clock turns orange (default 30). 0 = never.
 *   · Red at — minutes LEFT when it turns red (default 15). 0 = red only once over.
 *   (⛔⤴ F109 had hidden the orange lead; F155 brings it back as "Orange at".)
 * EMPTY means "the shipped default", shown as the placeholder, never pre-filled.
 * ⚠️ THE RANGES MATCH migrations 224 / 229's CHECK CONSTRAINTS AND THE ROUTE'S ZOD
 * (0..1440, 0..600, 0..600). If one changes, all three change.
 * ⚠️ Before migration 229 the red field is read-only and NOT sent — the route leaves
 * an absent red lead as stored, so saving the other two still works.
 */
function DetentionEditor(
    { facilityCode, getToken }: { facilityCode: string; getToken: () => Promise<string> },
) {
    const [freeText, setFreeText] = useState('');
    const [warnText, setWarnText] = useState('');
    const [urgentText, setUrgentText] = useState('');
    const [defaults, setDefaults] = useState<{ free: number; warn: number; urgent: number | null } | null>(null);
    const [migrated, setMigrated] = useState(true);
    const [urgentMigrated, setUrgentMigrated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!facilityCode) return;
        setLoading(true);
        setError(null);
        getToken()
            .then(t => apiFetch<{
                detentionFreeMinutes: number | null; detentionWarnMinutes: number | null;
                detentionUrgentMinutes?: number | null;
                defaultFreeMinutes: number; defaultWarnMinutes: number; defaultUrgentMinutes?: number;
                migrated: boolean; urgentMigrated?: boolean;
            }>(`mgmt/facilities/${facilityCode}/detention`, t))
            .then(r => {
                setFreeText(r.detentionFreeMinutes === null ? '' : String(r.detentionFreeMinutes));
                setWarnText(r.detentionWarnMinutes === null ? '' : String(r.detentionWarnMinutes));
                setUrgentText(r.detentionUrgentMinutes === null || r.detentionUrgentMinutes === undefined ? '' : String(r.detentionUrgentMinutes));
                setDefaults({ free: r.defaultFreeMinutes, warn: r.defaultWarnMinutes, urgent: r.defaultUrgentMinutes ?? null });
                setMigrated(r.migrated);
                setUrgentMigrated(r.urgentMigrated === true);
            })
            .catch(e => setError(e instanceof Error ? e.message : 'Failed to load the detention settings.'))
            .finally(() => setLoading(false));
    }, [facilityCode, getToken]);

    const parse = (t: string, hi: number) => {
        const x = t.trim();
        if (x === '') return { value: null as number | null, bad: false };
        const n = Number(x);
        return { value: n, bad: !Number.isInteger(n) || n < 0 || n > hi };
    };
    const free = parse(freeText, 1440);
    const warn = parse(warnText, 600);
    const urgent = parse(urgentText, 600);
    const invalid = free.bad || warn.bad || (urgentMigrated && urgent.bad);

    async function save() {
        if (invalid) return;
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${facilityCode}/detention`, token, {
                method: 'PUT',
                body: JSON.stringify({
                    detentionFreeMinutes: free.value,
                    detentionWarnMinutes: warn.value,
                    ...(urgentMigrated ? { detentionUrgentMinutes: urgent.value } : {}),
                }),
            });
            setSaved(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save.');
        } finally {
            setSaving(false);
        }
    }

    const box = (bad: boolean) =>
        `w-28 rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${bad ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;
    const ph = (n: number | null | undefined) => (defaults === null || n === null || n === undefined ? '—' : `${n} (default)`);

    return (
        <div className="rounded-md border border-gray-200 p-3 space-y-2">
            <div className="text-sm font-medium">Detention</div>
            <p className="text-[11px] text-gray-500">
                Each truck's clock starts at the later of its arrival and its booked time and counts down
                the time it has to load or unload. It shows in the arrived color, turns orange, then red,
                and once the time is up it counts up the minutes the truck is in detention. It stops when the
                truck departs. The driver cards and the warehouse board use the same numbers.
            </p>
            {!migrated && (
                <div className="text-[11px] text-amber-700">
                    The defaults are in force. Saving needs migration 224 to be run first.
                </div>
            )}
            {migrated && !urgentMigrated && (
                <div className="text-[11px] text-amber-700">
                    The red setting uses its default until migration 229 is run.
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-gray-500">Time to load or unload</span>
                <input
                    type="number" min={0} max={1440} value={freeText}
                    placeholder={ph(defaults?.free)}
                    title="Minutes. Leave empty for the default. 0 turns detention off at this facility."
                    disabled={loading || saving}
                    onChange={e => { setFreeText(e.target.value); setSaved(false); }}
                    className={box(free.bad)}
                />
                <span className="text-[11px] text-gray-500">minutes</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-orange-700">Orange at</span>
                <input
                    type="number" min={0} max={600} value={warnText}
                    placeholder={ph(defaults?.warn)}
                    title="Minutes left when the clock turns orange. Leave empty for the default. 0 = never orange."
                    disabled={loading || saving}
                    onChange={e => { setWarnText(e.target.value); setSaved(false); }}
                    className={box(warn.bad)}
                />
                <span className="text-[11px] text-red-700">Red at</span>
                <input
                    type="number" min={0} max={600} value={urgentText}
                    placeholder={ph(defaults?.urgent)}
                    title="Minutes left when the clock turns red. Leave empty for the default. 0 = red only once in detention."
                    disabled={loading || saving || !urgentMigrated}
                    onChange={e => { setUrgentText(e.target.value); setSaved(false); }}
                    className={box(urgentMigrated && urgent.bad)}
                />
                <span className="text-[11px] text-gray-500">minutes left</span>
                <button
                    onClick={() => void save()}
                    disabled={saving || loading || invalid}
                    className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {saved && <span className="text-[11px] text-green-700">Saved</span>}
            </div>
            {invalid && (
                <div className="text-[11px] text-red-600">
                    Time to load or unload: a whole number 0–1440. Orange and red: 0–600. Or leave empty for the default.
                </div>
            )}
            {error && <div className="text-[11px] text-red-600">{error}</div>}
        </div>
    );
}

/**
 * ⭐ F180 — GRACE TIMES (Decision 297, migration 235).
 *
 * > Pete, 2026-09-29: grace times *"Per facility, Settings → Appointments"*.
 *
 *   · On-time arrival tolerance — a truck within this many minutes either side of its
 *     booked start is on time; later is late, earlier is early (default 15). Timed
 *     slots only. Carrier punctuality in the reports reads it.
 *   · No-show grace — how long after the slot ends before an un-arrived appointment is
 *     a possible no-show (default 0 = as soon as the slot ends). The board mark and the
 *     Possible no-shows list both use it.
 * EMPTY means "the shipped default", shown as the placeholder, never pre-filled. The
 * defaults and the ranges come from the route (migration 235's CHECKs), not from here.
 */
function GraceEditor(
    { facilityCode, getToken }: { facilityCode: string; getToken: () => Promise<string> },
) {
    const [tolText, setTolText] = useState('');
    const [graceText, setGraceText] = useState('');
    const [cfg, setCfg] = useState<{ tolDefault: number; graceDefault: number; tolMax: number; graceMax: number } | null>(null);
    const [migrated, setMigrated] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!facilityCode) return;
        setLoading(true);
        setError(null);
        setSaved(false);
        getToken()
            .then(t => apiFetch<{
                onTimeToleranceMinutes: number | null; noShowGraceMinutes: number | null;
                defaultOnTimeToleranceMinutes: number; defaultNoShowGraceMinutes: number;
                maxOnTimeToleranceMinutes: number; maxNoShowGraceMinutes: number;
                migrated: boolean;
            }>(`mgmt/facilities/${facilityCode}/appointment-grace`, t))
            .then(r => {
                setTolText(r.onTimeToleranceMinutes === null ? '' : String(r.onTimeToleranceMinutes));
                setGraceText(r.noShowGraceMinutes === null ? '' : String(r.noShowGraceMinutes));
                setCfg({ tolDefault: r.defaultOnTimeToleranceMinutes, graceDefault: r.defaultNoShowGraceMinutes,
                         tolMax: r.maxOnTimeToleranceMinutes, graceMax: r.maxNoShowGraceMinutes });
                setMigrated(r.migrated);
            })
            .catch(e => setError(e instanceof Error ? e.message : 'Failed to load the grace times.'))
            .finally(() => setLoading(false));
    }, [facilityCode, getToken]);

    const parse = (t: string, hi: number | undefined) => {
        const x = t.trim();
        if (x === '') return { value: null as number | null, bad: false };
        const n = Number(x);
        return { value: n, bad: hi === undefined || !Number.isInteger(n) || n < 0 || n > hi };
    };
    const tol = parse(tolText, cfg?.tolMax);
    const grace = parse(graceText, cfg?.graceMax);
    const invalid = tol.bad || grace.bad;

    async function save() {
        if (invalid || !migrated) return;
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${facilityCode}/appointment-grace`, token, {
                method: 'PUT',
                body: JSON.stringify({ onTimeToleranceMinutes: tol.value, noShowGraceMinutes: grace.value }),
            });
            setSaved(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save.');
        } finally {
            setSaving(false);
        }
    }

    const box = (bad: boolean) =>
        `w-28 rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${bad ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;
    const ph = (n: number | undefined) => (n === undefined ? '—' : `${n} (default)`);

    return (
        <div className="rounded-md border border-gray-200 p-3 space-y-2" data-appointment-grace="">
            <div className="text-sm font-medium">Grace times</div>
            <p className="text-[11px] text-gray-500">
                On-time arrival: a truck that arrives within this many minutes before or after its booked
                time is on time (timed appointments only). No-show grace: how long after an appointment's
                slot ends, with no arrival, before it shows as a possible no-show.
            </p>
            {!migrated && (
                <div className="text-[11px] text-amber-700">
                    The defaults are in force. Saving needs migration 235 to be run first.
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-gray-500">On time within</span>
                <input
                    type="number" min={0} max={cfg?.tolMax} value={tolText}
                    placeholder={ph(cfg?.tolDefault)}
                    title="Minutes either side of the booked time. Leave empty for the default."
                    disabled={loading || saving || !migrated}
                    onChange={e => { setTolText(e.target.value); setSaved(false); }}
                    className={box(tol.bad)}
                />
                <span className="text-[11px] text-gray-500">minutes of the booked time</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-gray-500">Possible no-show</span>
                <input
                    type="number" min={0} max={cfg?.graceMax} value={graceText}
                    placeholder={ph(cfg?.graceDefault)}
                    title="Minutes after the slot ends. Leave empty for the default. 0 = as soon as the slot ends."
                    disabled={loading || saving || !migrated}
                    onChange={e => { setGraceText(e.target.value); setSaved(false); }}
                    className={box(grace.bad)}
                />
                <span className="text-[11px] text-gray-500">minutes after the slot ends</span>
                <button
                    onClick={() => void save()}
                    disabled={saving || loading || invalid || !migrated}
                    className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {saved && <span className="text-[11px] text-green-700">Saved</span>}
            </div>
            {invalid && cfg && (
                <div className="text-[11px] text-red-600">
                    On time within: a whole number 0–{cfg.tolMax}. Possible no-show: 0–{cfg.graceMax}. Or leave empty for the default.
                </div>
            )}
            {error && <div className="text-[11px] text-red-600">{error}</div>}
        </div>
    );
}

/**
 * ⭐⭐ F173 — DETENTION FREE TIME PER CLIENT AND DIRECTION (Decision 344, migration 231).
 *
 * > Pete: *"build the detention free timer per client and direction with a default for
 * > everyone that doesn't have a record at 2hours"* — after the Zevia contract: loaded
 * > *"within two (2) hours of the applicable appointment time"*.
 *
 * A client's row replaces the facility's time to load or unload for that client's trucks
 * (orange and red stay the facility's). Most specific wins: client + direction + facility
 * > client + direction > client + facility > client. Everyone without a row: the facility
 * time above, whose default is now 2 hours. ⛔ Company-bound on the server: the rows, the
 * client list and the facility list are the caller's company's only.
 */
type DetRule = { id?: number; clientCode: string; direction: 'INBOUND' | 'OUTBOUND' | null; facilityCode: string | null; freeMinutes: number; updatedBy?: string };

function DetentionRulesEditor(
    { facilityCode, getToken }: { facilityCode: string; getToken: () => Promise<string> },
) {
    const [rules, setRules] = useState<DetRule[]>([]);
    const [clients, setClients] = useState<Array<{ clientCode: string; clientName: string | null }>>([]);
    const [facilities, setFacilities] = useState<string[]>([]);
    const [defaultFree, setDefaultFree] = useState<number | null>(null);
    const [migrated, setMigrated] = useState(true);
    const [edits, setEdits] = useState<Record<number, string>>({});
    const [newClient, setNewClient] = useState('');
    const [newDir, setNewDir] = useState<'' | 'INBOUND' | 'OUTBOUND'>('');
    const [newFac, setNewFac] = useState('');
    const [newMin, setNewMin] = useState('');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const t = await getToken();
            const r = await apiFetch<{ rules: DetRule[]; migrated: boolean; clients: Array<{ clientCode: string; clientName: string | null }>; facilities: string[]; defaultFreeMinutes: number }>('mgmt/detention-rules', t);
            setRules(r.rules);
            setClients(r.clients);
            setFacilities(r.facilities);
            setDefaultFree(r.defaultFreeMinutes);
            setMigrated(r.migrated);
            setEdits({});
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load the client detention times.');
        }
    }, [getToken]);
    useEffect(() => { void load(); }, [load]);

    const minutesOk = (t: string) => { const n = Number(t.trim()); return t.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 1440; };

    async function save(rule: DetRule, minutesText: string) {
        if (!minutesOk(minutesText)) { setError('Minutes: a whole number 0–1440.'); return; }
        setBusy(true); setError(null); setNote(null);
        try {
            const t = await getToken();
            await apiFetch('mgmt/detention-rules', t, {
                method: 'PUT',
                body: JSON.stringify({ clientCode: rule.clientCode, direction: rule.direction, facilityCode: rule.facilityCode, freeMinutes: Number(minutesText.trim()) }),
            });
            setNote('Saved. The driver cards and the board use it on their next refresh.');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save.');
        } finally {
            setBusy(false);
        }
    }

    async function remove(rule: DetRule) {
        if (!rule.id) return;
        setBusy(true); setError(null); setNote(null);
        try {
            const t = await getToken();
            await apiFetch(`mgmt/detention-rules/${rule.id}`, t, { method: 'DELETE' });
            setNote('Removed. That client now uses the next most specific time.');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to remove.');
        } finally {
            setBusy(false);
        }
    }

    const dirLabel = (d: DetRule['direction']) => (d === 'INBOUND' ? 'Inbound (unload)' : d === 'OUTBOUND' ? 'Outbound (load)' : 'Both');
    const fmt = (m: number) => (m % 60 === 0 && m > 0 ? `${m / 60} h` : `${m} min`);
    // ⭐ the rows that can apply at the facility on screen first, then the others
    const shown = [...rules].sort((a, b) =>
        Number(b.facilityCode === null || b.facilityCode === facilityCode) - Number(a.facilityCode === null || a.facilityCode === facilityCode)
        || a.clientCode.localeCompare(b.clientCode));
    const box = 'rounded border border-gray-300 px-2 py-1 text-sm';

    return (
        <div className="rounded-md border border-gray-200 p-3 space-y-2" data-detention-rules="">
            <div className="text-sm font-medium">Time to load or unload, per client</div>
            <p className="text-[11px] text-gray-500">
                A client's own time replaces the facility's for that client's trucks. The most specific wins:
                client + direction + facility, then client + direction, then client + facility, then client.
                Everyone without a row uses the facility's time above{defaultFree !== null ? ` (default ${fmt(defaultFree)})` : ''}.
                Orange and red stay the facility's.
            </p>
            {!migrated && <div className="text-[11px] text-amber-700">Client times need migration 231 to be run first.</div>}
            {shown.length > 0 && (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                            <th className="py-1">Client</th><th>Direction</th><th>Facility</th><th>Minutes</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r) => {
                            const key = r.id ?? 0;
                            const text = edits[key] ?? String(r.freeMinutes);
                            const here = r.facilityCode === null || r.facilityCode === facilityCode;
                            return (
                                <tr key={key} className={`border-t border-gray-100 ${here ? '' : 'text-gray-400'}`} data-detention-rule={r.clientCode}>
                                    <td className="py-1">{r.clientCode}</td>
                                    <td>{dirLabel(r.direction)}</td>
                                    <td>{r.facilityCode ?? 'All facilities'}</td>
                                    <td>
                                        <input type="number" min={0} max={1440} value={text} disabled={busy}
                                               onChange={e => setEdits({ ...edits, [key]: e.target.value })}
                                               className={`w-24 ${box} ${minutesOk(text) ? '' : 'border-red-400 bg-red-50'}`} />
                                        <span className="ml-1 text-[11px] text-gray-500">{minutesOk(text) ? fmt(Number(text)) : ''}</span>
                                    </td>
                                    <td className="text-right whitespace-nowrap">
                                        <button onClick={() => void save(r, text)} disabled={busy || !minutesOk(text)}
                                                className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50 hover:bg-gray-50">Save</button>{' '}
                                        <button onClick={() => void remove(r)} disabled={busy}
                                                className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50 hover:bg-gray-50">Remove</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
                <select value={newClient} onChange={e => setNewClient(e.target.value)} disabled={busy || !migrated} className={box}>
                    <option value="">Client…</option>
                    {clients.map(c => <option key={c.clientCode} value={c.clientCode}>{c.clientName && c.clientName !== c.clientCode ? `${c.clientCode} — ${c.clientName}` : c.clientCode}</option>)}
                </select>
                <select value={newDir} onChange={e => setNewDir(e.target.value as '' | 'INBOUND' | 'OUTBOUND')} disabled={busy || !migrated} className={box}>
                    <option value="">Both directions</option>
                    <option value="INBOUND">Inbound (unload)</option>
                    <option value="OUTBOUND">Outbound (load)</option>
                </select>
                <select value={newFac} onChange={e => setNewFac(e.target.value)} disabled={busy || !migrated} className={box}>
                    <option value="">All facilities</option>
                    {facilities.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <input type="number" min={0} max={1440} value={newMin} placeholder="minutes" disabled={busy || !migrated}
                       onChange={e => setNewMin(e.target.value)} className={`w-24 ${box}`} />
                <button
                    onClick={() => { if (newClient) void save({ clientCode: newClient, direction: newDir || null, facilityCode: newFac || null, freeMinutes: 0 }, newMin).then(() => { setNewMin(''); }); }}
                    disabled={busy || !migrated || !newClient || !minutesOk(newMin)}
                    className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50 hover:bg-gray-50"
                >Add</button>
            </div>
            <div className="text-[11px] text-gray-500">0 minutes = no detention clock for that client.</div>
            {note && <div className="text-[11px] text-green-700">{note}</div>}
            {error && <div className="text-[11px] text-red-600">{error}</div>}
        </div>
    );
}

export default function FacilityHoursAdmin(
    { getToken, sections = ['hours', 'limits', 'dedications'] }:
    { getToken: () => Promise<string>; sections?: ('hours' | 'limits' | 'dedications' | 'search')[] }
) {
    const show = {
        hours: sections.includes('hours'),
        limits: sections.includes('limits'),
        dedications: sections.includes('dedications'),
        /** ⭐ #145 — the appointment-search window, on the Appointments tab. */
        search: sections.includes('search'),
    };
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [selectedCode, setSelectedCode] = useState<string>('');
    const [rows, setRows]       = useState<HoursRow[]>([]);
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState<string|null>(null);
    const [saved, setSaved]     = useState(false);

    useEffect(() => {
        getToken().then(async t => {
            const [res, me] = await Promise.all([
                apiFetch<{ facilities: Facility[] }>('mgmt/facilities', t),
                apiFetch<{ defaultFacilityCode: string | null }>('me', t).catch(() => null),
            ]);
            const facs = Array.isArray(res) ? res : (res?.facilities ?? []);
            setFacilities(facs);
            if (facs.length) {
                const codes = facs.map(f => f.FacilityCode);
                const preferred = me?.defaultFacilityCode && codes.includes(me.defaultFacilityCode)
                    ? me.defaultFacilityCode
                    : facs[0].FacilityCode;
                setSelectedCode(preferred);
            } else {
                setError('No facilities returned.');
            }
        }).catch(e => setError(e instanceof Error ? e.message : 'Failed to load facilities.'));
    }, [getToken]);

    const loadFacility = useCallback(async (code: string) => {
        setLoading(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            const [hoursData, holidayData] = await Promise.all([
                apiFetch<{ rows: HoursRow[] }>(`mgmt/facilities/${code}/hours`, token),
                apiFetch<Holiday[]>(`mgmt/facilities/${code}/holidays?days=180`, token),
            ]);
            setRows(hoursData.rows.length === 0
                ? buildDefaultSchedule()
                : hoursData.rows.map(r => ({ ...r, notes: r.notes??'', _key: `${r.dayOfWeek}:${r.direction}` }))
            );
            setHolidays(holidayData);
        } catch(e: unknown) {
            setError(e instanceof Error ? e.message : 'Load failed');
        } finally { setLoading(false); }
    }, [getToken]);

    useEffect(() => { if (selectedCode) loadFacility(selectedCode); }, [selectedCode, loadFacility]);

    const updateRow = (key: string, updates: Partial<HoursRow>) => {
        setRows(rs => rs.map(r => r._key===key ? {...r,...updates} : r));
        setSaved(false);
    };

    const splitRow = (row: HoursRow) => {
        const inn:  HoursRow = {...row, direction:'INBOUND',  _key:`${row.dayOfWeek}:INBOUND`};
        const out:  HoursRow = {...row, direction:'OUTBOUND', _key:`${row.dayOfWeek}:OUTBOUND`};
        setRows(rs => { const i=rs.findIndex(r=>r._key===row._key); const n=[...rs]; n.splice(i,1,inn,out); return n; });
        setSaved(false);
    };

    const mergeRow = (dow: number) => {
        setRows(rs => {
            const inRow = rs.find(r=>r.dayOfWeek===dow&&r.direction==='INBOUND');
            const merged = defaultRow(dow,'BOTH');
            if (inRow) { merged.isOpen=inRow.isOpen; merged.openTimeMinutes=inRow.openTimeMinutes; merged.closeTimeMinutes=inRow.closeTimeMinutes; merged.defaultSlotMinutes=inRow.defaultSlotMinutes; merged.notes=inRow.notes; }
            return [...rs.filter(r=>r.dayOfWeek!==dow), merged].sort((a,b)=>a.dayOfWeek-b.dayOfWeek||a.direction.localeCompare(b.direction));
        });
        setSaved(false);
    };

    const handleSave = async () => {
        if (!selectedCode) return;
        setSaving(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${selectedCode}/hours`, token, {
                method: 'PUT',
                body: JSON.stringify({ rows: rows.map(r=>({
                    dayOfWeek: r.dayOfWeek, direction: r.direction, isOpen: r.isOpen,
                    openTimeMinutes:    r.isOpen ? r.openTimeMinutes  : null,
                    closeTimeMinutes:   r.isOpen ? r.closeTimeMinutes : null,
                    defaultSlotMinutes: r.defaultSlotMinutes,
                    notes: r.notes||null,
                })) }),
            });
            setSaved(true);
        } catch(e: unknown) { setError(e instanceof Error ? e.message : 'Save failed.'); }
        finally { setSaving(false); }
    };

    const rowsByDay = SQL_DAYS.map(dow=>({ dow, dayName: DAY_NAMES[dow-1], rows: rows.filter(r=>r.dayOfWeek===dow) }));
    const isSplit   = (dow: number) => rows.filter(r=>r.dayOfWeek===dow).length===2;

    const selectedFac = facilities.find(f=>f.FacilityCode===selectedCode);

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
            {show.hours && (
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Facility Operating Hours</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Controls when carriers can request appointments. Holidays are automatically blocked.</p>
                </div>
            )}

            <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Facility</label>
                <select value={selectedCode} onChange={e=>setSelectedCode(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                    {facilities.map(f=>(
                        <option key={f.FacilityCode} value={f.FacilityCode}>{f.DisplayName} ({f.FacilityCode})</option>
                    ))}
                </select>
                {selectedFac && <span className="text-xs text-gray-400">{selectedFac.Timezone} · {selectedFac.WmsType}</span>}
            </div>

            {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
            {saved && <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">Hours saved successfully.</div>}

            {loading ? <div className="text-sm text-gray-400 animate-pulse py-8 text-center">Loading…</div> : (
                <>
                    {show.hours && (
                    <>
                    <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="bg-gray-50 grid grid-cols-[120px_100px_1fr_1fr_80px_1fr] gap-x-3 px-4 py-2.5 border-b border-gray-200">
                            {['Day','Open?','Opens','Closes','Slot min','Direction / Notes'].map(h=>(
                                <div key={h} className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</div>
                            ))}
                        </div>
                        {rowsByDay.map(({dow,dayName,rows:dayRows})=>(
                            <div key={dow} className="border-b border-gray-100 last:border-0">
                                {dayRows.map((row,i)=>(
                                    <div key={row._key} className={`grid grid-cols-[120px_100px_1fr_1fr_80px_1fr] gap-x-3 items-center px-4 py-2.5 ${!row.isOpen?'opacity-60':''} ${i>0?'border-t border-dashed border-gray-100':''}`}>
                                        <div className="text-sm font-medium text-gray-800">
                                            {i===0 ? dayName : ''}
                                            {i===0 && isSplit(dow) && (
                                                <button onClick={()=>mergeRow(dow)} className="ml-1.5 text-[10px] text-gray-400 hover:text-gray-600 underline">merge</button>
                                            )}
                                        </div>
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input type="checkbox" checked={row.isOpen} onChange={e=>updateRow(row._key,{isOpen:e.target.checked})}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"/>
                                            <span className="text-xs text-gray-600">{row.isOpen?'Open':'Closed'}</span>
                                        </label>
                                        <input type="time" disabled={!row.isOpen} value={minutesToTime(row.openTimeMinutes)}
                                            onChange={e=>updateRow(row._key,{openTimeMinutes:timeToMinutes(e.target.value)})}
                                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                                        <input type="time" disabled={!row.isOpen} value={minutesToTime(row.closeTimeMinutes)}
                                            onChange={e=>updateRow(row._key,{closeTimeMinutes:timeToMinutes(e.target.value)})}
                                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                                        <input type="number" min={15} max={480} step={15} disabled={!row.isOpen}
                                            value={row.defaultSlotMinutes??60}
                                            onChange={e=>updateRow(row._key,{defaultSlotMinutes:parseInt(e.target.value,10)||null})}
                                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                                        <div className="flex items-center gap-2">
                                            <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${row.direction==='INBOUND'?'bg-indigo-50 text-indigo-700':row.direction==='OUTBOUND'?'bg-orange-50 text-orange-700':'bg-gray-100 text-gray-600'}`}>
                                                {row.direction==='BOTH'?'↕ Both':row.direction==='INBOUND'?'↓ In':'↑ Out'}
                                            </span>
                                            {row.direction==='BOTH' && (
                                                <button onClick={()=>splitRow(row)} className="text-[10px] text-blue-500 hover:text-blue-700 underline shrink-0" title="Different hours for inbound vs outbound">split</button>
                                            )}
                                            <input type="text" placeholder="Notes" value={row.notes}
                                                onChange={e=>updateRow(row._key,{notes:e.target.value})}
                                                className="flex-1 min-w-0 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 placeholder-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400">Times are in the facility's local timezone ({selectedFac?.Timezone}). Holidays are blocked automatically.</p>
                        <button onClick={handleSave} disabled={saving}
                            className="px-5 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors">
                            {saving?'Saving…':'Save Hours'}
                        </button>
                    </div>
                    </>
                    )}

                    {show.limits && <FacilityCapacityEditor facilityCode={selectedCode} getToken={getToken} />}
                    {show.search && <SearchWindowEditor facilityCode={selectedCode} getToken={getToken} />}
                    {show.search && <DetentionEditor facilityCode={selectedCode} getToken={getToken} />}
                    {show.search && <DetentionRulesEditor facilityCode={selectedCode} getToken={getToken} />}
                    {show.search && <GraceEditor facilityCode={selectedCode} getToken={getToken} />}
                    {show.dedications && <FacilityDedicationsEditor facilityCode={selectedCode} getToken={getToken} />}

                    {show.hours && holidays.length>0 && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                            <h3 className="text-sm font-semibold text-amber-800">Upcoming Holidays (next 180 days)</h3>
                            <p className="text-xs text-amber-600">These dates are automatically blocked for appointment requests.</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                                {holidays.map(h=>(
                                    <div key={h.HolidayDate} className="bg-white rounded border border-amber-200 px-3 py-2">
                                        <div className="text-xs font-medium text-gray-700">
                                            {new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',year:'numeric'}).format(new Date(h.HolidayDate.split('T')[0]+'T12:00:00Z'))}
                                        </div>
                                        <div className="text-xs text-gray-500">{h.HolidayName}</div>
                                        {h.IsGlobal && <div className="text-[10px] text-amber-500 mt-0.5">All facilities</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

